<script setup>
import { ref, computed, watch } from 'vue';
import { useI18n } from 'vue-i18n';

const props = defineProps({
  content: { type: String, default: '' },
  mediaUrls: { type: Array, default: () => [] },
  // Vídeo (Reels etc.): a URL local (blob) não tem extensão, então o tipo
  // vem de fora.
  mediaIsVideo: { type: Boolean, default: false },
  username: { type: String, default: '' },
  platform: { type: String, default: 'instagram' },
});

const { t } = useI18n();
const activeMediaIndex = ref(0);
const captionExpanded = ref(false);

const displayUsername = computed(
  () => props.username || t('SOCIAL_STUDIO.EDITOR.PREVIEW.DEFAULT_USERNAME')
);
const avatarInitial = computed(() =>
  displayUsername.value.charAt(0).toUpperCase()
);
const visibleContent = computed(
  () => props.content || t('SOCIAL_STUDIO.EDITOR.PREVIEW.EMPTY_CONTENT')
);
const activeMedia = computed(() => props.mediaUrls[activeMediaIndex.value]);

watch(
  () => props.mediaUrls,
  () => {
    activeMediaIndex.value = 0;
  }
);

const previousMedia = () => {
  if (activeMediaIndex.value > 0) activeMediaIndex.value -= 1;
};

const nextMedia = () => {
  if (activeMediaIndex.value < props.mediaUrls.length - 1) {
    activeMediaIndex.value += 1;
  }
};
</script>

<template>
  <div
    class="overflow-hidden rounded-2xl border border-n-weak bg-n-solid-1 shadow-sm"
  >
    <div class="flex items-center justify-between gap-3 px-4 py-3">
      <div class="flex min-w-0 items-center gap-2.5">
        <span
          class="flex size-8 shrink-0 items-center justify-center rounded-full bg-gradient-to-br from-n-brand to-n-teal-9 text-xs font-bold text-white"
        >
          {{ avatarInitial }}
        </span>
        <div class="min-w-0">
          <p class="truncate text-xs font-semibold text-n-slate-12">
            {{ displayUsername }}
          </p>
          <p class="text-[10px] capitalize text-n-slate-9">{{ platform }}</p>
        </div>
      </div>
      <span class="i-lucide-more-horizontal size-4 text-n-slate-10" />
    </div>

    <div class="relative aspect-square bg-n-alpha-2">
      <video
        v-if="activeMedia && mediaIsVideo"
        :src="activeMedia"
        controls
        muted
        playsinline
        class="size-full object-cover"
      />
      <img
        v-else-if="activeMedia"
        :src="activeMedia"
        class="size-full object-cover"
        :alt="t('SOCIAL_STUDIO.EDITOR.PREVIEW.MEDIA_ALT')"
      />
      <div
        v-else
        class="flex size-full flex-col items-center justify-center gap-3 px-8 text-center"
      >
        <span
          class="flex size-12 items-center justify-center rounded-2xl bg-n-alpha-3 text-n-slate-9"
        >
          <span class="i-lucide-image size-5" />
        </span>
        <p class="text-xs leading-5 text-n-slate-9">
          {{ t('SOCIAL_STUDIO.EDITOR.PREVIEW.EMPTY_MEDIA') }}
        </p>
      </div>

      <template v-if="mediaUrls.length > 1">
        <button
          v-if="activeMediaIndex > 0"
          type="button"
          class="absolute left-2 top-1/2 flex size-7 -translate-y-1/2 items-center justify-center rounded-full bg-n-solid-1/90 text-n-slate-12 shadow"
          :aria-label="t('SOCIAL_STUDIO.EDITOR.PREVIEW.PREVIOUS')"
          @click="previousMedia"
        >
          <span class="i-lucide-chevron-left size-4" />
        </button>
        <button
          v-if="activeMediaIndex < mediaUrls.length - 1"
          type="button"
          class="absolute right-2 top-1/2 flex size-7 -translate-y-1/2 items-center justify-center rounded-full bg-n-solid-1/90 text-n-slate-12 shadow"
          :aria-label="t('SOCIAL_STUDIO.EDITOR.PREVIEW.NEXT')"
          @click="nextMedia"
        >
          <span class="i-lucide-chevron-right size-4" />
        </button>
        <span
          class="absolute right-2 top-2 rounded-full bg-n-alpha-black1 px-2 py-1 text-[10px] font-semibold text-white"
        >
          {{
            t('SOCIAL_STUDIO.EDITOR.PREVIEW.MEDIA_POSITION', {
              current: activeMediaIndex + 1,
              total: mediaUrls.length,
            })
          }}
        </span>
      </template>
    </div>

    <div class="space-y-2.5 px-4 py-3">
      <div class="flex items-center justify-between">
        <div class="flex items-center gap-3 text-n-slate-12">
          <span class="i-lucide-heart size-5" />
          <span class="i-lucide-message-circle size-5" />
          <span class="i-lucide-send size-5" />
        </div>
        <span class="i-lucide-bookmark size-5 text-n-slate-12" />
      </div>

      <div v-if="mediaUrls.length > 1" class="flex justify-center gap-1">
        <button
          v-for="(_, index) in mediaUrls"
          :key="index"
          type="button"
          class="size-1.5 rounded-full"
          :class="index === activeMediaIndex ? 'bg-n-brand' : 'bg-n-slate-6'"
          :aria-label="
            t('SOCIAL_STUDIO.EDITOR.PREVIEW.GO_TO_MEDIA', { index: index + 1 })
          "
          @click="activeMediaIndex = index"
        />
      </div>

      <p class="text-xs font-semibold text-n-slate-12">
        {{ t('SOCIAL_STUDIO.EDITOR.PREVIEW.LIKES') }}
      </p>
      <div class="text-xs leading-5 text-n-slate-11">
        <span class="mr-1 font-semibold text-n-slate-12">{{
          displayUsername
        }}</span>
        <span :class="captionExpanded ? '' : 'line-clamp-3'">{{
          visibleContent
        }}</span>
        <button
          v-if="content.length > 140"
          type="button"
          class="ml-1 text-n-slate-9"
          @click="captionExpanded = !captionExpanded"
        >
          {{
            captionExpanded
              ? t('SOCIAL_STUDIO.EDITOR.PREVIEW.LESS')
              : t('SOCIAL_STUDIO.EDITOR.PREVIEW.MORE')
          }}
        </button>
      </div>
    </div>
  </div>
</template>
